import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';

import { cn } from '../../../shared/lib/cn';
import { fullTime, logTime, spansDays, type ITimeSpan } from '../../../shared/lib/format';
import { ChevronDownIcon, PlayIcon, SourcesIcon, WaitIcon } from '../../../shared/ui/icons';
import {
  ShortId,
  Table,
  TruncateMiddle,
  type IColumn,
  type ISubRow,
} from '../../../shared/ui/table';
import { ErrorState, LoadingState } from '../../../shared/ui/states';
import {
  copyPathText,
  copyPlace,
  copyStatusView,
  getRun,
  runCopyCount,
  runKeys,
  runStatusView,
  RunStatus,
  startedBy,
  tookText,
  type TCopy,
  type TRun,
  type TRunColumn,
} from '../../../entities/run';
import { costText } from '../../../entities/agent-session';
import { IdCell } from './id-cell';

// 00-rules: "15:42:07 in tables" today, the day in front of it only when it is another day.
const rowTime = (iso: string, now: number, timeZone?: string): string =>
  logTime(iso, { from: iso, to: new Date(now).toISOString() }, timeZone);

export const RUNS_LAYOUT = 'event-runs';

const AUTH_WORD: Record<string, string> = {
  verified: 'Verified',
  failed: 'Unverified — kept anyway',
  skipped: 'Not checked',
};

interface IRunsTableProps {
  runs: readonly TRun[];
  columns: readonly TRunColumn[];
  paths: readonly string[];
  expanded: ReadonlySet<string>;
  onToggle: (runId: string) => void;
  onOpen: (run: TRun, copy?: TCopy) => void;
  now: number;
  span: ITimeSpan;
  timeZone?: string;
}

const StartedBy = ({ run, now, timeZone }: { run: TRun; now: number; timeZone?: string }) => {
  const by = startedBy(run, now, timeZone);
  const Icon = by.kind === 'request' ? SourcesIcon : by.kind === 'schedule' ? WaitIcon : PlayIcon;
  return (
    <span className="flex min-w-0 items-center gap-2">
      <span className="flex size-avatar shrink-0 items-center justify-center rounded-item bg-chip text-muted">
        <Icon size={12} />
      </span>
      {by.kind === 'request' ? (
        <span className="min-w-0 truncate font-mono">
          <ShortId id={by.id} />
        </span>
      ) : (
        <span className="truncate font-sans">{by.text}</span>
      )}
    </span>
  );
};

// A run column also says how a copy row under it reads.
interface IRunColumn extends IColumn<TRun> {
  copy: (copy: TCopy, run: TRun) => ReactNode;
}

type TSpec = Omit<IRunColumn, 'key'>;

const destinationsOf = (run: TRun): string =>
  [...new Set(run.copies.map((copy) => copy.destinationName).filter(Boolean))].join(', ') || '—';

const specs = (
  now: number,
  span: ITimeSpan,
  timeZone: string | undefined,
): Record<TRunColumn, TSpec> => {
  const time = spansDays(span, timeZone) ? 'w-col-time-dated' : 'w-col-time';
  return {
    status: {
      header: 'Status',
      width:
        'w-col-run-status @max-runs-fit:w-col-run-status-narrow @max-runs-fit:min-w-col-run-status-narrow @max-runs-fit:grow',
      ui: true,
      // The source promises "kept, delivered, and marked unverified": this is the mark.
      render: (run) => (
        <span className="flex min-w-0 items-center gap-2">
          <RunStatus view={runStatusView(run, now, timeZone)} />
          {run.authResult === 'failed' && (
            <span
              title="The sender check failed; the source keeps such requests and marks them"
              className="shrink-0 rounded-tag bg-danger-surface px-1 font-mono text-micro text-danger-text"
            >
              unverified
            </span>
          )}
        </span>
      ),
      copy: (copy) => (
        <span className="block pl-4">
          <RunStatus view={copyStatusView(copy, now, timeZone)} />
        </span>
      ),
    },
    id: {
      header: 'Run',
      width: 'w-col-run-id @max-runs-fit:w-col-run-id-narrow',
      render: (run) => <IdCell id={run.id} />,
      copy: (copy) => (
        <span className="flex min-w-0 items-center gap-2">
          <IdCell id={copy.id} />
          {copyPlace(copy) && (
            <span className="shrink-0 text-meta text-faint">{copyPlace(copy)}</span>
          )}
        </span>
      ),
    },
    flow: {
      header: 'Flow',
      width:
        'min-w-col-flow-name @max-runs-fit:min-w-col-run-flow-narrow @max-runs-fit:max-w-col-run-flow-narrow',
      grow: true,
      ui: true,
      render: (run) => (
        <span className="text-primary" title={run.summary.text}>
          {run.flowName ?? 'A deleted flow'}
        </span>
      ),
      copy: (copy) => (
        <span className="font-mono text-meta text-muted">
          <TruncateMiddle text={copyPathText(copy) || '—'} />
        </span>
      ),
    },
    by: {
      header: 'Started by',
      width: 'w-col-run-by @max-runs-fit:w-col-run-by-narrow',
      ui: true,
      render: (run) => <StartedBy run={run} now={now} timeZone={timeZone} />,
      copy: () => null,
    },
    type: {
      header: 'Event type',
      width: 'w-col-event-type',
      ui: true,
      render: (run) => <TruncateMiddle text={run.eventType ?? '—'} />,
      copy: (copy) => <TruncateMiddle text={copy.eventType ?? '—'} />,
    },
    destination: {
      header: 'Destinations',
      width: 'w-col-destination',
      ui: true,
      render: (run) => destinationsOf(run),
      copy: (copy) => copy.destinationName ?? '—',
    },
    client: {
      header: 'Customer',
      width: 'w-col-client',
      ui: true,
      render: (run) => run.customerExternalId ?? '—',
      copy: () => null,
    },
    auth: {
      header: 'Sender check',
      width: 'w-col-run-auth',
      ui: true,
      render: (run) => (run.authResult ? (AUTH_WORD[run.authResult] ?? run.authResult) : '—'),
      copy: () => null,
    },
    copies: {
      header: 'Copies',
      width: 'w-col-run-count',
      align: 'right',
      render: (run) => runCopyCount(run).toLocaleString('en-US'),
      copy: () => null,
    },
    agentSteps: {
      header: 'Agent steps',
      width: 'w-col-agent-steps',
      align: 'right',
      hint: 'Model and tool calls of its Agent steps.',
      render: (run) => (run.agentSteps === null ? '—' : run.agentSteps.toLocaleString('en-US')),
      copy: () => null,
    },
    agentCost: {
      header: 'Cost',
      width: 'w-col-agent-cost',
      align: 'right',
      hint: 'What its agents’ model calls spent.',
      render: (run) => (run.agentCostUsd === null ? '—' : costText(run.agentCostUsd)),
      copy: () => null,
    },
    took: {
      header: 'Took',
      width: 'w-col-run-took @max-runs-fit:w-col-run-took-narrow',
      align: 'right',
      render: (run) => tookText(run),
      copy: () => <span className="text-faint">—</span>,
    },
    started: {
      header: 'Started',
      width: time,
      align: 'right',
      render: (run) => (
        <span title={fullTime(run.at, timeZone)}>{rowTime(run.at, now, timeZone)}</span>
      ),
      copy: (copy) => (
        <span title={fullTime(copy.at, timeZone)}>{rowTime(copy.at, now, timeZone)}</span>
      ),
    },
  };
};

const pathColumn = (path: string): IRunColumn => ({
  key: `path:${path}`,
  header: path,
  width: 'w-col-run-payload',
  render: (run) => <TruncateMiddle text={run.columns?.[path] ?? '—'} />,
  copy: () => null,
});

const CopyRows = ({
  run,
  drawRow,
  onOpen,
}: {
  run: TRun;
  drawRow: (sub: ISubRow<IRunColumn>) => ReactNode;
  onOpen: (run: TRun, copy?: TCopy) => void;
}) => {
  // The list carries ten copies at most; a run with more reads its own detail (up to 500).
  const detail = useQuery({
    queryKey: runKeys.detail(run.id),
    queryFn: () => getRun(run.id, run.at),
    enabled: run.copiesTruncated,
  });

  if (run.copiesTruncated && detail.isPending) {
    return <LoadingState rows={2} className="px-page py-2" />;
  }
  if (run.copiesTruncated && detail.isError) {
    return (
      <div className="px-page py-2">
        <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />
      </div>
    );
  }

  const copies = (detail.data?.copies ?? run.copies)
    .slice()
    .sort((a, b) => (a.total > 0 ? a.index : 1e6) - (b.total > 0 ? b.index : 1e6));

  return (
    <>
      {copies.length > 1 && (
        <p className="flex h-row items-center border-b border-border-row bg-surface-sunken pr-inset pl-10 font-mono text-meta text-muted">
          {run.summary.text}
        </p>
      )}
      {copies.map((copy) =>
        drawRow({
          key: copy.id,
          cell: (column) => column.copy(copy, run),
          onOpen: () => onOpen(run, copy),
          height: 'lg',
          className: 'bg-surface-sunken',
        }),
      )}
      {run.copiesTruncated && detail.data?.copiesTruncated && (
        <p className="border-b border-border-row bg-surface-sunken px-page py-2 text-meta text-faint">
          The first {detail.data.copies.length.toLocaleString('en-US')} copies are shown; open the
          run to follow any of them.
        </p>
      )}
    </>
  );
};

export const RunsTable = ({
  runs,
  columns,
  paths,
  expanded,
  onToggle,
  onOpen,
  now,
  span,
  timeZone,
}: IRunsTableProps) => {
  const all = specs(now, span, timeZone);
  // Payload columns sit before the times, next to the flow they came through.
  const TAIL: readonly TRunColumn[] = ['copies', 'took', 'started'];
  const tail = columns.filter((key) => TAIL.includes(key));
  const head = columns.filter((key) => !TAIL.includes(key));
  const cells: IRunColumn[] = [
    ...head.map((key) => ({ key, ...all[key] })),
    ...paths.map(pathColumn),
    ...tail.map((key) => ({ key, ...all[key] })),
  ];
  // Without the Flow column something still has to take the spare width.
  const last = head.length - 1;
  if (!cells.some((cell) => cell.grow) && last >= 0) cells[last] = { ...cells[last]!, grow: true };

  return (
    <Table<TRun, IRunColumn>
      layoutId={RUNS_LAYOUT}
      columns={cells}
      rows={runs}
      rowKey={(run) => run.id}
      height="xl"
      onRowClick={(run) => onOpen(run)}
      rowClassName={(run) => expanded.has(run.id) && 'bg-surface-raised'}
      onRowKeyDown={(run, event) => {
        const open = expanded.has(run.id);
        if (event.key === 'ArrowRight' && !open) onToggle(run.id);
        if (event.key === 'ArrowLeft' && open) onToggle(run.id);
      }}
      gutter={(run) => {
        if (run.copies.length === 0) return null;
        const open = expanded.has(run.id);
        return (
          <button
            type="button"
            aria-expanded={open}
            aria-label={open ? 'Hide its copies' : 'Show its copies'}
            onClick={(event) => {
              event.stopPropagation();
              onToggle(run.id);
            }}
            onKeyDown={(event) => event.stopPropagation()}
            className="flex size-avatar items-center justify-center rounded-item text-faint hover:bg-ghost hover:text-primary"
          >
            <ChevronDownIcon size={12} className={cn(!open && '-rotate-90')} />
          </button>
        );
      }}
      expanded={(run, drawRow) =>
        expanded.has(run.id) && <CopyRows run={run} drawRow={drawRow} onOpen={onOpen} />
      }
    />
  );
};
